import { Controller } from '@hotwired/stimulus'
import fetchWithHandlingLoginRedirect from '../../modules/utils/fetchWithHandlingLoginRedirect'
import escapeHtml from '../../utils/sanitize'
import {
  trans,
  PHOTOS_MAX_REACHED,
  PHOTO_UPLOAD_FAILED,
  PHOTO_INVALID_FILE,
  SET_PRIMARY,
  DELETE_PHOTO
} from '../../translator'

interface UploadedPhoto {
  media_id: string
  url: string
  thumbnail_url: string
}

/**
 * Manages the Associated Person photo carousel on the create/edit form.
 *
 * Photos are uploaded immediately (minting a Media row + S3 object server-side)
 * but only linked to the person on save: the controller maintains a hidden
 * `photo_media_ids[]` input per photo (DOM order = position) plus a single
 * `primary_media_id` input, which the form controller's buildPayload reads. The
 * newest upload becomes primary; a per-thumbnail star promotes any photo and
 * updates the Identity-section primary photo. Delete removes a thumbnail immediately (no
 * dialog) — nothing is persisted until Save. lightGallery (loaded globally by the
 * record-media bundle) provides full-size viewing when present.
 */
export default class AssociatedPersonPhotosController extends Controller {
  static targets = ['primaryPhoto', 'placeholder', 'addBelow', 'trigger', 'input', 'carousel', 'primaryInput', 'maxMessage']
  static values = {
    uploadUrl: { type: String, default: '/internal/api/associated-person/photo' },
    maxPhotos: { type: Number, default: 20 },
    imageMaxSize: Number,
    allowedTypes: String
  }

  declare readonly primaryPhotoTarget: HTMLImageElement
  declare readonly hasPrimaryPhotoTarget: boolean
  declare readonly placeholderTarget: HTMLElement
  declare readonly hasPlaceholderTarget: boolean
  declare readonly addBelowTarget: HTMLElement
  declare readonly hasAddBelowTarget: boolean
  declare readonly inputTarget: HTMLInputElement
  declare readonly hasInputTarget: boolean
  declare readonly carouselTarget: HTMLElement
  declare readonly hasCarouselTarget: boolean
  declare readonly primaryInputTarget: HTMLInputElement
  declare readonly hasPrimaryInputTarget: boolean
  declare readonly maxMessageTarget: HTMLElement
  declare readonly hasMaxMessageTarget: boolean
  declare readonly uploadUrlValue: string
  declare readonly maxPhotosValue: number
  declare readonly imageMaxSizeValue: number
  declare readonly allowedTypesValue: string

  connect (): void {
    this.initLightGallery()
    this.refreshState()
  }

  /**
   * Opens the native file dialog (bound to the photo box click). Uses showPicker
   * rather than input.click() because app.js installs a global jQuery handler that
   * preventDefaults clicks on any [data-action] element (and its ancestors, such as
   * the surrounding .card), which cancels a click-triggered dialog. showPicker is an
   * imperative call unaffected by the cancelled click default.
   */
  openDialog (): void {
    // At capacity the file input is disabled, so showPicker() would be a silent no-op (a dead
    // button). Surface the "Maximum N photos" note instead. The note clears via updateCapacity
    // once a photo is removed and the count drops below the cap.
    if (this.atCapacity()) {
      this.showMaxMessage()

      return
    }

    if (this.hasInputTarget) {
      this.inputTarget.showPicker()
    }
  }

  onInputChange (event: Event): void {
    const input = event.target as HTMLInputElement
    if (input.files !== null) {
      // eslint-disable-next-line no-void
      void this.uploadFiles(input.files)
    }
    input.value = ''
  }

  onDrop (event: DragEvent): void {
    event.preventDefault()
    if (event.dataTransfer !== null) {
      // eslint-disable-next-line no-void
      void this.uploadFiles(event.dataTransfer.files)
    }
  }

  allowDrop (event: DragEvent): void {
    event.preventDefault()
  }

  /**
   * Uploads a multi-file selection one at a time. Awaiting each upload is what makes the
   * per-file allowance check correct: a thumbnail is only appended after its upload resolves,
   * so uploading in parallel would let every file in the batch pass the pre-upload capacity
   * check and overrun the cap. Uploading in turn keeps atCapacity() accurate between files.
   */
  async uploadFiles (files: FileList): Promise<void> {
    for (const file of Array.from(files)) {
      if (this.atCapacity()) {
        this.showMaxMessage()

        return
      }
      if (this.isValid(file)) {
        await this.upload(file)
      }
    }
  }

  async upload (file: File): Promise<void> {
    if (this.atCapacity()) {
      this.showMaxMessage()

      return
    }
    if (!this.isValid(file)) {
      return
    }

    const data = new FormData()
    data.append('file', file)

    try {
      const response = await fetchWithHandlingLoginRedirect(this.uploadUrlValue, { method: 'POST', body: data })

      if (!response.ok) {
        this.showMaxMessage(trans(PHOTO_UPLOAD_FAILED, {}, 'associated-persons'))

        return
      }

      const photo = await response.json() as UploadedPhoto
      this.appendThumbnail(photo)
    } catch {
      this.showMaxMessage(trans(PHOTO_UPLOAD_FAILED, {}, 'associated-persons'))
    }
  }

  private appendThumbnail (photo: UploadedPhoto): void {
    if (!this.hasCarouselTarget) {
      return
    }

    const node = document.createElement('div')
    node.className = 'ap-photo'
    node.setAttribute('data-ap-photo', '')
    // dataset / setAttribute escape on their own; only innerHTML interpolation needs escapeHtml.
    node.dataset.mediaId = photo.media_id
    node.dataset.url = photo.url
    node.innerHTML = `
      <input type="hidden" name="photo_media_ids[]" value="${escapeHtml(photo.media_id)}">
      <a class="ap-photo__image rounded overflow-hidden d-block" href="${escapeHtml(photo.url)}" data-sub-html="" style="width:90px;height:90px;">
        <img src="${escapeHtml(photo.thumbnail_url)}" alt="" class="ap-photo__thumb" style="width:100%;height:100%;object-fit:cover;">
      </a>
      <div class="d-flex justify-content-center mt-1">
        <button type="button" class="ap-photo__delete btn btn-sm btn-clear text-dark p-1" data-action="associated-person-photos#remove" aria-label="${escapeHtml(trans(DELETE_PHOTO, {}, 'associated-persons'))}"><i class="fas fa-times"></i></button>
        <button type="button" class="ap-photo__primary btn btn-sm btn-clear text-dark p-1" data-action="associated-person-photos#setPrimary" aria-label="${escapeHtml(trans(SET_PRIMARY, {}, 'associated-persons'))}"><i class="far fa-star"></i></button>
      </div>
    `

    this.carouselTarget.appendChild(node)
    // Newest upload becomes primary.
    this.setPrimaryMediaId(photo.media_id)
    this.refreshState()
    this.initLightGallery()
  }

  remove (event: Event): void {
    const node = (event.target as Element).closest('[data-ap-photo]')
    if (node === null) {
      return
    }

    const wasPrimary = (node as HTMLElement).dataset.mediaId === this.primaryInputTarget.value
    node.remove()

    if (wasPrimary) {
      // Promote the newest remaining photo.
      const remaining = this.photoNodes()
      const newest = remaining.length > 0 ? (remaining[remaining.length - 1] as HTMLElement).dataset.mediaId ?? '' : ''
      this.setPrimaryMediaId(newest)
    }

    this.refreshState()
  }

  setPrimary (event: Event): void {
    const node = (event.target as Element).closest('[data-ap-photo]')
    if (node === null) {
      return
    }

    this.setPrimaryMediaId((node as HTMLElement).dataset.mediaId ?? '')
    this.refreshState()
  }

  private setPrimaryMediaId (mediaId: string): void {
    if (this.hasPrimaryInputTarget) {
      this.primaryInputTarget.value = mediaId
    }
  }

  /** Reconciles star fills, the primary photo and the at-capacity affordance with the DOM. */
  private refreshState (): void {
    const nodes = this.photoNodes()
    const primaryId = this.primaryInputTarget.value

    nodes.forEach(node => {
      const isPrimary = (node as HTMLElement).dataset.mediaId === primaryId && primaryId !== ''
      node.classList.toggle('ap-photo--primary', isPrimary)
      const star = node.querySelector('.ap-photo__primary i')
      if (star !== null) {
        star.className = isPrimary ? 'fas fa-star' : 'far fa-star'
      }
    })

    this.updatePrimaryPhoto(primaryId)
    this.updateCapacity(nodes.length)
  }

  private updatePrimaryPhoto (primaryId: string): void {
    if (!this.hasPrimaryPhotoTarget) {
      return
    }

    const primaryNode = Array.from(this.photoNodes()).find(node => (node as HTMLElement).dataset.mediaId === primaryId) as HTMLElement | undefined

    const hasPrimary = undefined !== primaryNode && primaryId !== ''

    if (hasPrimary) {
      this.primaryPhotoTarget.setAttribute('src', primaryNode?.dataset.url ?? '')
    } else {
      this.primaryPhotoTarget.removeAttribute('src')
    }

    this.primaryPhotoTarget.classList.toggle('d-none', !hasPrimary)
    if (this.hasPlaceholderTarget) {
      this.placeholderTarget.classList.toggle('d-none', hasPrimary)
    }
    if (this.hasAddBelowTarget) {
      this.addBelowTarget.classList.toggle('d-none', !hasPrimary)
    }
  }

  private updateCapacity (count: number): void {
    const atCap = count >= this.maxPhotosValue
    if (this.hasInputTarget) {
      this.inputTarget.disabled = atCap
    }
    if (this.hasMaxMessageTarget && !atCap) {
      this.maxMessageTarget.classList.add('d-none')
    }
  }

  private showMaxMessage (message?: string): void {
    if (!this.hasMaxMessageTarget) {
      return
    }

    this.maxMessageTarget.textContent = message ?? trans(PHOTOS_MAX_REACHED, { '%max%': String(this.maxPhotosValue) }, 'associated-persons')
    this.maxMessageTarget.classList.remove('d-none')
  }

  private isValid (file: File): boolean {
    const allowed = this.allowedTypesValue.split(',').map(type => type.trim()).filter(type => type !== '')
    const typeOk = allowed.length === 0 || allowed.includes(file.type)
    const sizeOk = !this.imageMaxSizeValue || file.size <= this.imageMaxSizeValue

    if (!typeOk || !sizeOk) {
      this.showMaxMessage(trans(PHOTO_INVALID_FILE, {}, 'associated-persons'))

      return false
    }

    return true
  }

  private atCapacity (): boolean {
    return this.photoNodes().length >= this.maxPhotosValue
  }

  private photoNodes (): NodeListOf<Element> {
    return this.hasCarouselTarget ? this.carouselTarget.querySelectorAll('[data-ap-photo]') : document.querySelectorAll('[data-ap-photo]')
  }

  private initLightGallery (): void {
    const lg = (window as unknown as { lightGallery?: (el: Element, opts: object) => void }).lightGallery
    if (typeof lg === 'function' && this.hasCarouselTarget) {
      // Items are .ap-photo wrappers; point lightGallery at the inner anchors so it
      // reads each href rather than a wrapper div with no src.
      lg(this.carouselTarget, { mode: 'lg-fade', thumbnail: true, selector: '.ap-photo__image' })
    }
  }
}
